import React from "react";
import { calculateEstimate } from "lib/calculateEstimate";
import { createSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "lib/utils";

const DonationWeightChart = ({
  totalWeight: totalWeightRaw = 0,
  replayWeight: replayWeightRaw = 0,
  replayPercentage: replayPercentageRaw = 0,
  showEditLink = false,
  queryParams = {},
  className = "",
  rewardCaption = "Estimated reward",
  showBackground = false,
  showTotalEstimatedWeight = false,
}) => {
  const totalWeight = parseFloat(totalWeightRaw);
  const replayWeight = parseFloat(replayWeightRaw);
  const replayPercentage = parseFloat(replayPercentageRaw);

  const remainingWeight = totalWeight - replayWeight;

  const { t } = useTranslation();

  const getBarStyles = () => {
    if (replayWeight === 0) {
      return {
        container: {
          width: "100%",
          height: "5px",
          backgroundColor: "#4CAF50", // green
        },
      };
    }

    if (replayWeight === totalWeight) {
      return {
        container: {
          width: "100%",
          height: "5px",
          backgroundColor: "#4C2F92",
        },
      };
    }

    return {
      container: {
        width: "100%",
        height: "8px",
        display: "flex",
        gap: parseFloat(replayPercentage) > 0 ? "4px" : "0px",
      },
      donationBar: {
        width: `${(remainingWeight?.toFixed(2) / totalWeight?.toFixed(2)) * 100}%`,
        height: "100%",
      },
      replayBar: {
        width: `${(replayWeight?.toFixed(2) / totalWeight?.toFixed(2)) * 100}%`,
        height: "100%",
      },
    };
  };

  const styles = getBarStyles();

  return (
    <div
      className={cn(
        className,
        showBackground && "bg-gray-300/25 px-6 py-4 rounded-lg"
      )}
    >
      {showTotalEstimatedWeight && (
        <div className="text-secondary mb-3 text-sm">
          Total estimated weight:{" "}
          <strong>{parseFloat(totalWeight).toFixed(2)} lbs</strong>
        </div>
      )}
      <div style={styles.container}>
        <div style={styles.donationBar} className="bg-green" />
        <div style={styles.replayBar} className="bg-[#4C2F92]" />
      </div>
      <div className="flex flex-col md:flex-row md:flex-wrap md:gap-2 mt-3 md:items-center text-sm">
        <div className="flex space-x-2 items-center whitespace-nowrap flex-shrink-0">
          <div className="w-3 h-3 bg-green rounded-sm"></div>
          <div>
            <span className="text-secondary">
              {rewardCaption} ({100 - parseFloat(replayPercentage)}%):
            </span>{" "}
            <span className="font-bold">
              ${calculateEstimate(remainingWeight).gift} (
              {remainingWeight.toFixed(2)} lbs)
            </span>
          </div>
        </div>
        <div className="flex space-x-2 items-center whitespace-nowrap flex-shrink-0">
          <div className="w-3 h-3 bg-[#4C2F92] rounded-sm"></div>
          <div>
            <span className="text-secondary">
              Donation ({replayPercentage}%):
            </span>{" "}
            <span className="font-bold">
              {parseFloat(replayWeight).toFixed(2)} lbs
            </span>
          </div>
        </div>
        {showEditLink && (
          <Link
            to={`${t("urls.trade_in_donation_adapt")}?${createSearchParams(
              queryParams
            ).toString()}`}
            className="flex space-x-1 items-center text-blue group cursor-pointer text-sm mt-2 md:mt-0"
          >
            <span className="material-symbols-outlined scale-75 mb-[1px]">
              edit
            </span>
            <span className="group-hover:underline">Edit</span>
          </Link>
        )}
      </div>
    </div>
  );
};

export default DonationWeightChart;
